import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { omit } from 'lodash';
import { OrderPageIntent } from '../OrderPageIntent';
import { AccountData } from '../../api';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageState } from './OrderPageState';
import { setUTMData } from '../orderCompletion/utmLocalStorage';
import { clearOrderPageStateFromLocalStorage, getOrderPageStateFromLocalStorage } from './orderPageStateLocalStorage';

type UseInitPassOrderPageStateProps = {
  orderPageState: OrderPageState;
  setOrderPageState: (orderPageState: OrderPageState) => void;
  account: AccountData;
};

export const useInitPassOrderPageState = ({
  orderPageState,
  setOrderPageState,
  account,
}: UseInitPassOrderPageStateProps) => {
  const { orderPageId } = useParams();
  const [searchParams] = useSearchParams();
  const troubleshoot = searchParams.get('troubleshoot') === 'true';
  const language = useLanguage();
  const { i18n } = useTranslation();
  const posthog = usePostHog();
  const experience = account.experiences[0];

  useEffect(() => {
    if (!account) return;

    if (account.marketing_integrations?.gtm_id) {
      TagManager.initialize({ gtmId: account.marketing_integrations.gtm_id });
    }

    const accountDefaultLanguage = account.default_system_language;
    if (accountDefaultLanguage !== language) {
      void i18n.changeLanguage(accountDefaultLanguage);
    }

    const passOrderPage = account.pass_order_pages?.find((p: any) => p.id === orderPageId);
    if (!passOrderPage) {
      return;
    }

    posthog.setPersonPropertiesForFlags({ accountId: account.id });
    posthog.setPersonProperties({
      accountId: account.id,
      accountName: account.name,
      orderPageId: passOrderPage?.id,
      environment: import.meta.env.VITE_REACT_APP_ENVIRONMENT || 'local',
      context: 'pass-order-page',
    });

    const localStorageState = passOrderPage ? getOrderPageStateFromLocalStorage(passOrderPage?.id) : null;
    const queryParamsChanged =
      localStorageState?.savedQueryString !== undefined &&
      localStorageState.savedQueryString !== window.location.search;
    if (queryParamsChanged) {
      clearOrderPageStateFromLocalStorage();
    }

    if (localStorageState && !queryParamsChanged) {
      setOrderPageState({
        ...orderPageState,
        ...omit(localStorageState, ['account', 'orderPage']),
        orderPage: passOrderPage,
        account,
        experience,
        orderPageIntent: OrderPageIntent.BUY_PASS,
        isLoadingDates: false,
        specificOrderPage: { ...(localStorageState.specificOrderPage || {}), troubleshoot },
      });
    } else {
      setOrderPageState({
        ...orderPageState,
        account,
        experience,
        orderPage: passOrderPage,
        orderPageIntent: OrderPageIntent.BUY_PASS,
        isLoadingDates: false,
        isInitialized: true,
        specificOrderPage: { ...orderPageState.specificOrderPage, troubleshoot },
        details: {
          ...orderPageState.details,
          persons: 1,
          price: passOrderPage?.price || 0,
        },
      });
    }

    if (troubleshoot) {
      posthog.capture('Troubleshoot started', { orderPageId: passOrderPage?.id });
    }

    setUTMData();
  }, [account]);
};
